import { mq, Spacer } from '@kvdbil/components';
import React from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import PriceInformation from '~/App/views/ObjectPage/components/BiddingView/components/PriceInformation';
import Services from '~/App/views/ObjectPage/components/BiddingView/components/AdditionalServices';
import {
  allBids as allBidsSelector,
  objectViewDrawerAuction,
  objectViewDrawerBuyMethod,
  objectViewDrawerFlow
} from '~/App/shared/selectors/storeObject';
import { session as sessionSelector } from '~/App/shared/selectors/sessionSelector';
import { websocketBidsById } from '~/App/shared/selectors/websockets';
import { wonAuctions as wonAuctionsSelector } from '~/App/shared/selectors/wonAuctions';
import { FINANCING_MINIMUM_AMOUNT } from '~/config/constants';
import {
  isRecreationalVehicle as isRecreationalVehicleHelper,
  processAuction,
  processAuctionStates
} from '~/helpers/auction';
import { useTranslation } from '~/Locale';
import Bidding from './components/Bidding';
import BiddingViewTopRow, {
  BiddingViewSeparator
} from './components/BiddingViewTopRow';
import { useGA4ObjectEvent } from '~/App/shared/hooks/useGA4ObjectEvent';
import { AuctionType } from '~/App/shared/interfaces/Auction';
import FixedPrice from './components/FixedPrice';
import { IncludeContent } from '~/App/shared/components/MediaQueryContainer';
import {
  auctionTypeIsBidding,
  auctionTypeIsFixedPrice
} from '~/helpers/buyCar';
import { isHeavyEquipmentObject } from '~/App/views/ObjectPage/helpers';
import HeavyEquipmentServices from './components/HeavyEquipmentServices';
import { unionBy } from '~/helpers/unionBy';

/* The &:after element is added due to fact that
browsers wrongly (or not intuitively) interpret bottom padding when
element is scrollable */
const Container = styled.div`
  padding: 2.5rem;
  height: 100%;

  ${mq('mobileL', 'laptop')} {
    padding: 2rem;

    &:after {
      display: block;
      content: '';
      width: 100%;
      height: 4rem;
    }
  }

  ${mq(null, 'mobileL')} {
    padding: 1rem;
  }

  ${mq('laptop')} {
    padding-top: 3rem;
  }
`;

const ColumnContainer = styled.div`
  height: auto;
  min-height: 100%;
  max-width: 1032px;
  margin: auto;

  &:after {
    clear: both;
  }
`;

interface ColumnProps {
  left?: boolean;
  right?: boolean;
}

// TODO: fix the random styles in here
// If you remove any paddings or margins, make sure to remove it on RegisterContainer in BiddingLogin as well
const Column = styled.div<ColumnProps>`
  float: left;
  width: 50%;
  position: relative;
  margin-top: 4rem;
  margin-bottom: 4rem;

  ${mq(null, 'laptop')} {
    margin-top: 0;
    margin-bottom: 0;
    padding-bottom: 4rem;
    padding-top: 4rem;
  }

  ${mq(null, 'laptop')} {
    float: none;
    width: 100%;
  }

  ${({ left }) =>
    left &&
    css`
      ${mq('laptop')} {
        padding: 0 2.5rem 1rem 0;
      }

      ${mq(null, 'laptop')} {
        border-left: none;
      }
    `}
  ${({ right, theme }) =>
    right &&
    css`
      ${mq('laptop')} {
        border-left: 1px solid ${theme.colors.grayscaleToned.light6};
        padding: 0 0 4rem 2.5rem;
      }
    `}
`;

export const BiddingView = () => {
  const session = useSelector(sessionSelector);
  const allBids = useSelector(allBidsSelector);
  const wonAuctions = useSelector(wonAuctionsSelector);
  const auction = useSelector(objectViewDrawerAuction);
  const flow = useSelector(objectViewDrawerFlow);
  const buyMethod = useSelector(objectViewDrawerBuyMethod);
  const websocketBids = useSelector(websocketBidsById);

  const { t } = useTranslation();

  useGA4ObjectEvent('view_bidding_modal', auction);

  if (!auction) return null;

  const union = unionBy([...Object.values(websocketBids), ...allBids], 'id');
  const processedAuction = processAuction(t, {
    auction: auction,
    websocketBids: union
  });

  const auctionStates = processAuctionStates({
    auction: processedAuction,
    session,
    wonAuctions: wonAuctions
  });

  const vehicleType = auction?.processObject?.objectType ?? '';
  const isRecreationalVehicle = isRecreationalVehicleHelper(vehicleType);
  const isHeavyEquipment = isHeavyEquipmentObject(auction?.processObject);
  const previewImage =
    auction?.processObject.media.find(
      m => m.contentType === 'image' && m.type === 'PRESENTATION'
    )?.uri ?? '';

  const { isClosed } = auctionStates;

  const eligibleForAdditionalServices = auction.eligibleForAdditionalServices;
  const leadingBidAmount = processedAuction?.leadingBid?.amount ?? 0;

  const isBuyNow = buyMethod == AuctionType.BUY_NOW;
  const auctionCost = isBuyNow
    ? (auction.buyNowAmount ?? 0)
    : leadingBidAmount || auction.startBid || FINANCING_MINIMUM_AMOUNT;

  const getServices = () => {
    if (isHeavyEquipment) {
      return <HeavyEquipmentServices />;
    }

    if (eligibleForAdditionalServices) {
      if (auctionTypeIsBidding(auction?.auctionType)) {
        return (
          <Services
            auction={auction}
            buyMethod={buyMethod}
            auctionCost={auctionCost}
          />
        );
      }

      if (auctionTypeIsFixedPrice(auction?.auctionType)) {
        return (
          <Services
            buyMethod={buyMethod}
            auctionCost={auctionCost}
            auction={auction}
            showReleasingForm={true}
            isFinanceServiceVisible={flow !== 'releasing'}
            isServiceAgreementReleasingVisible={flow === 'releasing'}
          />
        );
      }
    }

    return null;
  };

  return (
    <Container>
      <BiddingViewTopRow
        auction={processedAuction}
        previewImage={previewImage}
        isClosed={isClosed}
      />
      <ColumnContainer>
        <Column left>
          {buyMethod === AuctionType.BIDDING ? (
            <Bidding auction={processedAuction} auctionStates={auctionStates} />
          ) : (
            <FixedPrice auction={processedAuction} />
          )}

          <IncludeContent to="laptop">
            <BiddingViewSeparator />
          </IncludeContent>
        </Column>
        <Column right>
          {getServices()}
          <Spacer height={4} />

          <PriceInformation
            auctionObject={auction}
            isRecreationalVehicle={isRecreationalVehicle}
          />
        </Column>
      </ColumnContainer>
    </Container>
  );
};

export default BiddingView;
